/**
 * Amounts as people type them (ADR-0112 amendment 3, the till's "Invalid amount" on `0.250`):
 * Arabic-Indic digits, a comma or the Arabic decimal separator instead of the point, thousands
 * marks, spaces, bidi marks and a currency word before or after the number. `normalizeAmountInput`
 * turns them into the plain decimal `Money.parse` and `parseQuantity` accept, and leaves anything
 * it cannot read for those to refuse.
 */

import { parseQuantity, QuantityError } from './quantity.js';

const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
const EXTENDED_ARABIC_INDIC = '۰۱۲۳۴۵۶۷۸۹';

/** `"٠٫٢٥٠"` → `"0.250"`, `"0,250"` → `"0.250"`, `"1,250.500 KWD"` → `"1250.500"`. */
export function normalizeAmountInput(raw: string): string {
  let s = '';
  for (const ch of raw) {
    const a = ARABIC_INDIC.indexOf(ch);
    const e = EXTENDED_ARABIC_INDIC.indexOf(ch);
    s += a >= 0 ? String(a) : e >= 0 ? String(e) : ch;
  }
  s = s
    // Bidi marks and every kind of space.
    .replace(/[\u200e\u200f\u061c\u202a-\u202e\u2066-\u2069]/g, '')
    .replace(/[\s\u00a0\u202f]+/g, '')
    // Arabic decimal and thousands separators.
    .replace(/\u066b/g, '.')
    .replace(/\u066c/g, ',')
    // A word or currency symbol before the first digit or after the last one ("KWD", "د.ك",
    // "فلس"). A letter between digits stays, so "5x2" is refused instead of read as 52.
    .replace(/^[\p{L}\p{Sc}.]*[\p{L}\p{Sc}]/u, '')
    .replace(/[\p{L}\p{Sc}][\p{L}\p{Sc}.]*$/u, '')
    // Points left behind by "د.ك" before or after the number.
    .replace(/\.+$/g, '')
    .replace(/^\.+(?=.*\.)/, '');
  // A comma is the decimal separator when it is the only one ("0,250"); next to a point it is a
  // thousands mark ("1,250.500").
  if (s.includes(',') && !s.includes('.')) {
    const last = s.lastIndexOf(',');
    s = `${s.slice(0, last).replace(/,/g, '')}.${s.slice(last + 1)}`;
  } else s = s.replace(/,/g, '');
  if (s.startsWith('.')) s = `0${s}`;
  if (s.startsWith('-.')) s = `-0${s.slice(1)}`;
  if (s.startsWith('+')) s = s.slice(1);
  return s;
}

/**
 * A whole number typed in Arabic or Latin digits (BR-250: counts, minutes, days, limits) as
 * Latin text, or `''` when it is not one — what a browser number field reports.
 */
export function wholeNumberInput(raw: string): string {
  const s = normalizeAmountInput(raw);
  return /^\d{1,9}$/.test(s) ? s : '';
}

/** `parseQuantity` of what a person typed (BR-250): `"١٫٥"` → `1500n`. Throws `QuantityError`. */
export function parseQuantityInput(raw: string): bigint {
  return parseQuantity(normalizeAmountInput(raw));
}

/** `parseQuantityInput` for a form field: the quantity, or `null` when it is not one. Signed. */
export function tryParseQuantityInput(raw: string): bigint | null {
  try {
    return parseQuantityInput(raw);
  } catch (error) {
    if (error instanceof QuantityError) return null;
    throw error;
  }
}
